import { useEffect, useMemo, useState } from "react";
import { X, Copy, Mail, ExternalLink, Check, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import { adminApi, formatAdminError } from "@/lib/adminApi";
import { GhostButton, PrimaryButton } from "@/components/admin/uiKit";

// Mirror of backend stripe_service.calculate_plan_price_cents — same
// pricing constants live in Plans.jsx public copy, so this stays in sync
// with the public site's Step-5 math.
const EXTRA_BIN = 800;
function computeTotalCents(plan, bins) {
  const n = Math.max(1, parseInt(bins || 1, 10));
  if (plan === "monthly") return 2500 + Math.max(0, n - 2) * EXTRA_BIN;
  if (plan === "biweekly") return 4000 + Math.max(0, n - 2) * EXTRA_BIN;
  if (plan === "onetime") return 4000 * n;
  return 0;
}
function fmtPrice(plan, bins) {
  const cents = computeTotalCents(plan, bins);
  const dollars = `$${(cents / 100).toFixed(0)}`;
  if (plan === "onetime") return `${dollars} one-time`;
  return `${dollars} / month`;
}

const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const PLANS = [
  ["monthly", "Monthly · $25 + $8/extra bin"],
  ["biweekly", "Bi-Weekly · $40/mo + $8/extra bin"],
  ["onetime", "One-Time · $40 / bin"],
];

function todayPlus(days) {
  const d = new Date();
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
}

export default function ManualSignupDialog({ open, onClose, onCreated }) {
  const [form, setForm] = useState(() => ({
    full_name: "",
    email: "",
    phone: "",
    address_line1: "",
    city: "",
    state: "NJ",
    zip: "",
    plan_code: "biweekly",
    bin_count: 2,
    service_day: "Wed",
    service_date: todayPlus(7),
    internal_notes: "",
    payment_mode: "record_only",
  }));
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const [result, setResult] = useState(null); // {customer_id, payment_link, status, total_cents}
  const [sendingEmail, setSendingEmail] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);

  useEffect(() => {
    if (open) {
      document.body.style.overflow = "hidden";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));
  const previewLabel = useMemo(
    () => fmtPrice(form.plan_code, form.bin_count),
    [form.plan_code, form.bin_count],
  );

  if (!open) return null;

  const validate = () => {
    const e = {};
    if (!form.full_name.trim() || form.full_name.trim().length < 2) e.full_name = "Required";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = "Valid email required";
    if (!form.phone.replace(/\D/g, "").length) e.phone = "Required";
    if (!form.address_line1.trim()) e.address_line1 = "Required";
    if (!/^\d{5}$/.test(form.zip)) e.zip = "5 digits";
    if (!form.service_date) e.service_date = "Pick a date";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submit = async () => {
    if (!validate()) return;
    setSubmitting(true);
    try {
      const body = {
        ...form,
        bin_count: parseInt(form.bin_count, 10),
        email: form.email.trim().toLowerCase(),
        full_name: form.full_name.trim(),
        phone: form.phone.trim(),
        zip: form.zip.trim(),
        internal_notes: form.internal_notes.trim() || null,
      };
      const { data } = await adminApi.post("/admin/customers/manual", body);
      setResult(data);
      if (data.status === "manual_unpaid") {
        toast.success("Customer recorded.");
        onCreated?.();
        // Auto-close shortly so the list refresh is felt
        setTimeout(() => handleClose(), 600);
      } else {
        toast.success("Customer added — payment link generated.");
        onCreated?.();
      }
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (detail?.error === "email_already_registered") {
        setErrors((e) => ({ ...e, email: "Email already in use" }));
        toast.error("Email already registered.");
      } else {
        toast.error(formatAdminError(err));
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    setResult(null);
    setForm((p) => ({
      ...p,
      full_name: "",
      email: "",
      phone: "",
      address_line1: "",
      city: "",
      zip: "",
      internal_notes: "",
      bin_count: 2,
      plan_code: "biweekly",
      payment_mode: "record_only",
    }));
    setErrors({});
    setLinkCopied(false);
    onClose?.();
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(result.payment_link);
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 1500);
    } catch {
      toast.error("Could not copy link.");
    }
  };

  const sendEmail = async () => {
    if (!result?.customer_id) return;
    setSendingEmail(true);
    try {
      await adminApi.post(`/admin/customers/${result.customer_id}/send-payment-link`);
      toast.success("Payment link emailed.");
    } catch (err) {
      toast.error(formatAdminError(err));
    } finally {
      setSendingEmail(false);
    }
  };

  // Step 2 view — payment link result panel
  if (result && result.status === "manual_pending_payment") {
    return (
      <Backdrop onClose={handleClose}>
        <div className="p-6 sm:p-8 space-y-5" data-testid="manual-signup-result">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-[10px] uppercase tracking-[0.24em] text-slate-500 font-bold">Manual signup</p>
              <h2 className="text-2xl font-extrabold text-slate-900 mt-1" style={{ fontFamily: "Outfit, ui-sans-serif" }}>
                Payment link ready
              </h2>
            </div>
            <CloseButton onClick={handleClose} />
          </div>

          <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-900 flex items-start gap-2.5">
            <Check size={18} className="mt-0.5 shrink-0" />
            <div>
              <p className="font-semibold">Customer created · status: manual_pending_payment</p>
              <p className="text-emerald-800/80 text-xs mt-0.5">{result.message}</p>
            </div>
          </div>

          <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
            <p className="text-[10px] uppercase tracking-[0.22em] text-slate-500 font-bold mb-2">Stripe Checkout link</p>
            <p
              className="break-all text-xs text-slate-700 font-mono bg-white border border-slate-200 rounded-md px-3 py-2"
              data-testid="manual-signup-payment-link"
            >
              {result.payment_link}
            </p>
            <div className="mt-3 flex flex-wrap items-center gap-2">
              <button
                type="button"
                onClick={copyLink}
                className="inline-flex items-center gap-2 h-10 px-4 rounded-lg border border-slate-200 bg-white text-sm font-bold text-slate-800 hover:bg-slate-50"
                data-testid="manual-signup-copy-link"
              >
                {linkCopied ? <Check size={14} className="text-emerald-600" /> : <Copy size={14} />}
                {linkCopied ? "Copied" : "Copy Link"}
              </button>
              <a
                href={result.payment_link}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-2 h-10 px-4 rounded-lg border border-slate-200 bg-white text-sm font-bold text-slate-800 hover:bg-slate-50"
              >
                <ExternalLink size={14} /> Open
              </a>
              <button
                type="button"
                onClick={sendEmail}
                disabled={sendingEmail}
                className="inline-flex items-center gap-2 h-10 px-4 rounded-lg bg-sky-500 text-white text-sm font-bold hover:bg-sky-600 disabled:opacity-60"
                data-testid="manual-signup-send-email"
              >
                <Mail size={14} /> {sendingEmail ? "Sending…" : "Send via Email"}
              </button>
            </div>
          </div>

          <p className="text-xs text-slate-500">
            Total: <strong>${(result.total_cents / 100).toFixed(2)}</strong> · You can re-send this link from the customer's detail drawer any time.
          </p>

          <div className="pt-2 flex justify-end">
            <PrimaryButton onClick={handleClose} data-testid="manual-signup-done">Done</PrimaryButton>
          </div>
        </div>
      </Backdrop>
    );
  }

  return (
    <Backdrop onClose={handleClose}>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          submit();
        }}
        className="p-6 sm:p-8 space-y-5"
        data-testid="manual-signup-form"
      >
        <div className="flex items-start justify-between">
          <div>
            <p className="text-[10px] uppercase tracking-[0.24em] text-slate-500 font-bold">Manual signup</p>
            <h2 className="text-2xl font-extrabold text-slate-900 mt-1" style={{ fontFamily: "Outfit, ui-sans-serif" }}>
              Add a customer
            </h2>
            <p className="text-sm text-slate-500 mt-1">For walk-ups, phone signups, and old-school folks.</p>
          </div>
          <CloseButton onClick={handleClose} />
        </div>

        <Field label="Full name" error={errors.full_name}>
          <input
            value={form.full_name}
            onChange={(e) => set("full_name", e.target.value)}
            className={inputCls(errors.full_name)}
            data-testid="manual-signup-full-name"
            autoFocus
          />
        </Field>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="Email" error={errors.email}>
            <input
              type="email"
              value={form.email}
              onChange={(e) => set("email", e.target.value)}
              className={inputCls(errors.email)}
              data-testid="manual-signup-email"
            />
          </Field>
          <Field label="Phone" error={errors.phone}>
            <input
              value={form.phone}
              onChange={(e) => set("phone", e.target.value)}
              className={inputCls(errors.phone)}
              data-testid="manual-signup-phone"
            />
          </Field>
        </div>

        <Field label="Address line 1" error={errors.address_line1}>
          <input
            value={form.address_line1}
            onChange={(e) => set("address_line1", e.target.value)}
            className={inputCls(errors.address_line1)}
            data-testid="manual-signup-address1"
          />
        </Field>

        <div className="grid grid-cols-12 gap-3">
          <div className="col-span-6">
            <Field label="City">
              <input
                value={form.city}
                onChange={(e) => set("city", e.target.value)}
                className={inputCls()}
                data-testid="manual-signup-city"
              />
            </Field>
          </div>
          <div className="col-span-2">
            <Field label="State">
              <input
                value={form.state}
                onChange={(e) => set("state", e.target.value.toUpperCase().slice(0, 2))}
                className={inputCls()}
                data-testid="manual-signup-state"
              />
            </Field>
          </div>
          <div className="col-span-4">
            <Field label="ZIP" error={errors.zip}>
              <input
                value={form.zip}
                onChange={(e) => set("zip", e.target.value.replace(/\D/g, "").slice(0, 5))}
                className={inputCls(errors.zip)}
                inputMode="numeric"
                data-testid="manual-signup-zip"
              />
            </Field>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="Plan">
            <select
              value={form.plan_code}
              onChange={(e) => set("plan_code", e.target.value)}
              className={inputCls()}
              data-testid="manual-signup-plan"
            >
              {PLANS.map(([v, l]) => (
                <option key={v} value={v}>{l}</option>
              ))}
            </select>
          </Field>
          <Field label="Bin count">
            <div className="flex items-stretch h-11 rounded-lg border border-slate-200 overflow-hidden" data-testid="manual-signup-bin-stepper">
              <button
                type="button"
                onClick={() => set("bin_count", Math.max(1, form.bin_count - 1))}
                className="w-12 bg-slate-50 hover:bg-slate-100 font-bold text-slate-700"
              >−</button>
              <input
                type="number"
                min={1}
                max={20}
                value={form.bin_count}
                onChange={(e) => set("bin_count", Math.max(1, Math.min(20, parseInt(e.target.value || 1, 10))))}
                className="flex-1 text-center font-bold text-slate-900 focus:outline-none"
                data-testid="manual-signup-bin-count"
              />
              <button
                type="button"
                onClick={() => set("bin_count", Math.min(20, form.bin_count + 1))}
                className="w-12 bg-slate-50 hover:bg-slate-100 font-bold text-slate-700"
              >+</button>
            </div>
          </Field>
        </div>

        <div className="rounded-xl bg-slate-900 text-white px-4 py-3 flex items-baseline justify-between" data-testid="manual-signup-price-preview">
          <span className="text-[10px] uppercase tracking-[0.22em] text-slate-300 font-bold">Today&rsquo;s total</span>
          <span className="text-xl font-extrabold tracking-tight" style={{ fontFamily: "Outfit, ui-sans-serif" }}>{previewLabel}</span>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="Service day">
            <select
              value={form.service_day}
              onChange={(e) => set("service_day", e.target.value)}
              className={inputCls()}
              data-testid="manual-signup-service-day"
            >
              {DAYS.map((d) => (
                <option key={d} value={d}>{d}</option>
              ))}
            </select>
          </Field>
          <Field label="First service date" error={errors.service_date}>
            <input
              type="date"
              min={todayPlus(1)}
              value={form.service_date}
              onChange={(e) => set("service_date", e.target.value)}
              className={inputCls(errors.service_date)}
              data-testid="manual-signup-service-date"
            />
          </Field>
        </div>

        <Field label="Internal notes (optional)">
          <textarea
            rows={2}
            value={form.internal_notes}
            onChange={(e) => set("internal_notes", e.target.value)}
            className={`${inputCls()} h-auto py-2.5 leading-snug`}
            data-testid="manual-signup-notes"
            placeholder="Met at the BBQ. Prefers texts."
          />
        </Field>

        <div>
          <p className="text-[10px] font-bold uppercase tracking-[0.22em] text-slate-500 mb-2">Payment handling</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5" role="radiogroup" aria-label="Payment handling">
            <RadioOption
              value="record_only"
              current={form.payment_mode}
              onChange={(v) => set("payment_mode", v)}
              title="Record only"
              hint="No charge attempted. For paper-trail customers."
              testId="manual-signup-mode-record"
            />
            <RadioOption
              value="payment_link"
              current={form.payment_mode}
              onChange={(v) => set("payment_mode", v)}
              title="Send payment link"
              hint="Generate a Stripe Checkout link to share."
              testId="manual-signup-mode-link"
            />
          </div>
        </div>

        <div className="pt-2 flex flex-col-reverse sm:flex-row sm:justify-end gap-3">
          <GhostButton onClick={handleClose} type="button">Cancel</GhostButton>
          <PrimaryButton
            type="submit"
            disabled={submitting}
            data-testid="manual-signup-submit"
            className="w-full sm:w-auto"
          >
            {submitting
              ? "Saving…"
              : form.payment_mode === "payment_link"
                ? "Add Customer & Generate Payment Link"
                : "Add Customer"}
          </PrimaryButton>
        </div>
      </form>
    </Backdrop>
  );
}

function Backdrop({ children, onClose }) {
  return (
    <div className="fixed inset-0 z-50" role="dialog" aria-modal="true" data-testid="manual-signup-dialog">
      <button
        type="button"
        aria-label="Dismiss"
        onClick={onClose}
        className="absolute inset-0 bg-slate-950/50 backdrop-blur-sm"
      />
      <div className="absolute inset-0 flex items-start sm:items-center justify-center p-0 sm:p-6 overflow-y-auto">
        <div className="relative bg-white w-full sm:max-w-xl sm:rounded-2xl shadow-2xl min-h-screen sm:min-h-0 sm:max-h-[92vh] overflow-y-auto">
          {children}
        </div>
      </div>
    </div>
  );
}

function CloseButton({ onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="h-9 w-9 rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50 grid place-items-center shrink-0"
      data-testid="manual-signup-close"
    >
      <X size={16} />
    </button>
  );
}

function Field({ label, error, children }) {
  return (
    <label className="block">
      <span className="block text-[10px] font-bold uppercase tracking-[0.22em] text-slate-500 mb-1">{label}</span>
      {children}
      {error && (
        <span className="mt-1 flex items-center gap-1 text-[11px] text-rose-600 font-semibold">
          <AlertCircle size={11} /> {error}
        </span>
      )}
    </label>
  );
}

function inputCls(hasError) {
  return [
    "w-full h-11 rounded-lg border bg-white px-3 text-sm focus:outline-none focus:ring-2",
    hasError
      ? "border-rose-300 focus:border-rose-400 focus:ring-rose-100"
      : "border-slate-200 focus:border-sky-400 focus:ring-sky-100",
  ].join(" ");
}

function RadioOption({ value, current, onChange, title, hint, testId }) {
  const active = current === value;
  return (
    <button
      type="button"
      onClick={() => onChange(value)}
      className={[
        "text-left rounded-xl border px-4 py-3 transition-colors",
        active
          ? "border-sky-400 bg-sky-50 ring-2 ring-sky-100"
          : "border-slate-200 bg-white hover:border-slate-300",
      ].join(" ")}
      role="radio"
      aria-checked={active}
      data-testid={testId}
    >
      <div className="flex items-center gap-2.5">
        <span
          className={[
            "h-4 w-4 rounded-full border grid place-items-center",
            active ? "border-sky-500 bg-white" : "border-slate-300",
          ].join(" ")}
        >
          {active && <span className="h-2 w-2 rounded-full bg-sky-500" />}
        </span>
        <span className="font-bold text-slate-900 text-sm">{title}</span>
      </div>
      <p className="mt-1.5 text-xs text-slate-500 leading-snug">{hint}</p>
    </button>
  );
}
